import { defineStore } from "pinia";
import { ref } from "vue";

export interface Account {
  username: string;
  modules: string[];
}

export const useAuthStore = defineStore("auth", () => {
  const account = ref<Account | null>(null);
  const loading = ref<boolean>(true);

  async function login(username: string, password: string): Promise<boolean> {
    loading.value = true;
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({username, password}),
      });
      if (!response.ok) {
        account.value = null;
        return false;
      }
      const body = await response.json();
      account.value = body as Account;
      return true;
    } catch {
      account.value = null;
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    loading.value = true;
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
      });
    } finally {
      account.value = null;
      loading.value = false;
    }
  }

  async function fetchAccount(): Promise<void> {
    loading.value = true;
    try {
      const response = await fetch("/api/auth/account");
      if (!response.ok) {
        account.value = null;
        return;
      }
      account.value = await response.json() as Account;
    } catch {
      account.value = null;
    } finally {
      loading.value = false;
    }
  }

  function hasAllModules(module: string[]): boolean {
    if (!account.value) return false;
    return module.every(m => account.value!.modules.includes(m));
  }

  fetchAccount().then();

  return { account, login, loading, logout, hasAllModules };
})
